import type { SaunaCommandResult } from '@jums-dashboard/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { abortSauna, acknowledgeSaunaReady, finishSauna, startSauna } from '../services/saunaService';
import { saunaQueryKey } from './useSauna';
import { saunaSessionHistoryQueryKey } from './useSaunaSessionHistory';

interface Vars {
  homeId: string;
}

function useSaunaCommand(fn: (homeId: string) => Promise<SaunaCommandResult>, failureMessage: string, successMessage?: string) {
  const qc = useQueryClient();
  return useMutation<SaunaCommandResult, Error, Vars>({
    mutationFn: ({ homeId }) => fn(homeId),
    onSuccess: (data, { homeId }) => {
      if (successMessage && !data.noop) {
        toast.success(successMessage);
      }
      void qc.invalidateQueries({ queryKey: saunaQueryKey(homeId) });
      void qc.invalidateQueries({ queryKey: saunaSessionHistoryQueryKey(homeId) });
    },
    onError: (err) => {
      toast.error(err.message || failureMessage);
    },
  });
}

export function useStartSauna() {
  return useSaunaCommand(startSauna, 'Failed to start the sauna.', 'Sauna session started.');
}

export function useFinishSauna() {
  return useSaunaCommand(finishSauna, 'Failed to end the sauna session.', 'Sauna session ended.');
}

export function useAcknowledgeSaunaReady() {
  return useSaunaCommand(acknowledgeSaunaReady, 'Failed to acknowledge the sauna.', 'Sauna marked as in use.');
}

export function useAbortSauna() {
  return useSaunaCommand(abortSauna, 'Failed to cancel the sauna session.', 'Sauna session cancelled.');
}
